import { DashboardUrl, getSearchLocationRequest, purchaseCampaignByIdRequest } from "@common";
import { TimetableData } from "@components";
import {
    AppState,
    campaignBudgetForm,
    campaignCreativesForm,
    campaignMediaPlanForm,
    CampaignMediaPlanFormFields,
    changeSaveOrPurchaseProcessingAction,
    openNotificationAction,
    purchaseCampaignByIdAction
} from "@store";
import { FETCH_STATE_SUCCESS } from "rd-redux-http";
import { replace } from "react-router-redux";
import { call, put, select } from "redux-saga/effects";
import { createOrUpdateCampaign } from "./createOrUpdateCampaign";
import { selectPurchasedCampaignNameAction } from "../../campaignList";

export function* purchaseCampaign(action: typeof purchaseCampaignByIdAction.typeInterface) {
    const { campaignId, clientId } = action;

    let state: AppState = yield select();

    const mediaPlanForm = campaignMediaPlanForm.selector(state.campaignState.campaignMediaPlanForm);
    const budgetForm = campaignBudgetForm.selector(state.campaignState.campaignBudgetForm);

    // const requiredFields: Array<keyof CampaignMediaPlanFormFields> = ["zipCode", "country", "region", "city"];
    const requiredFields: Array<keyof CampaignMediaPlanFormFields> = ["country"];
    const emptyRequiredFields = requiredFields.filter(f => !mediaPlanForm.fields[f].value);

    if (emptyRequiredFields.length) {
        const errors = {
            country: ["Please fill all required fields"]
        };

        const actualErrors = emptyRequiredFields.reduce((err, field) => {
            err[field] = errors[field];
            return err;
        }, {});

        yield put(campaignMediaPlanForm.actions.validate());
        yield put(campaignMediaPlanForm.actions.setErrors({ fields: actualErrors }));
        yield put(
            openNotificationAction({
                isOpen: true,
                isWarning: true,
                message: "Please fill all required fields"
            })
        );

        return;
    }

    const region: string = mediaPlanForm.fields.region.value;
    const place: string = mediaPlanForm.fields.city.value;
    const zip: string = mediaPlanForm.fields.zipCode.value;

    try {
        if (region && (place || zip)) {
            const response = yield* getSearchLocationRequest.saga({ "search-place": place, "search-zip": zip, region });

            if (response.length === 0) {
                yield put(campaignMediaPlanForm.actions.validate());
                yield put(
                    campaignMediaPlanForm.actions.setErrors({
                        fields: {
                            city: ["You should put existing city"],
                            zipCode: ["You should put existing zip-code"]
                        }
                    })
                );
                yield put(
                    openNotificationAction({
                        isOpen: true,
                        isWarning: true,
                        message: "You should put existing zip-code or city"
                    })
                );
                return;
            }
        }

        const timetable: TimetableData = mediaPlanForm.fields.timetable.value;
        let isTimetableValid: boolean = false;

        for (let day in timetable) {
            if (!isTimetableValid) {
                isTimetableValid = timetable[day]["morning"] === true ? true : isTimetableValid;
                isTimetableValid = timetable[day]["afternoon"] === true ? true : isTimetableValid;
                isTimetableValid = timetable[day]["night"] === true ? true : isTimetableValid;
            }
        }

        if (mediaPlanForm.fields.priceModel.value !== "CPC" && !isTimetableValid) {
            yield put(campaignMediaPlanForm.actions.validate());
            yield put(
                campaignMediaPlanForm.actions.setErrors({
                    fields: { timetable: ["You shoud chose time"] }
                })
            );
            yield put(
                openNotificationAction({ isOpen: true, isWarning: true, message: "You shoud choose time in timetable" })
            );
            return;
        }

        if (budgetForm.fields.budget.value <= 0) {
            yield put(campaignMediaPlanForm.actions.validate());
            yield put(
                campaignMediaPlanForm.actions.setErrors({
                    fields: { timetable: ["Budget shoud be more than 0"] }
                })
            );
            yield put(
                openNotificationAction({ isOpen: true, isWarning: true, message: "Please, choose budget more than 0" })
            );
            return;
        }

        const startDate = new Date(mediaPlanForm.fields.startDate.value).valueOf();
        const endDate = new Date(mediaPlanForm.fields.endDate.value).valueOf();

        if (startDate >= endDate) {
            yield put(campaignMediaPlanForm.actions.validate());
            yield put(
                campaignMediaPlanForm.actions.setErrors({
                    fields: {
                        startDate: ["Start date shoud be earlier than end date"],
                        endDate: ["Start date shoud be earlier than end date"]
                    }
                })
            );

            yield put(
                openNotificationAction({
                    isOpen: true,
                    isWarning: true,
                    message: "Please, chose start date earlier than end date"
                })
            );
            return;
        }

        yield put(changeSaveOrPurchaseProcessingAction({ isProcessing: true }));

        if (campaignId) {
            yield call(createOrUpdateCampaign, {
                clientId: clientId,
                campaignId: campaignId,
                mode: "purchase save"
            });
        } else {
            yield call(createOrUpdateCampaign, { clientId: clientId, mode: "purchase new campaign" });
        }

        state = yield select();

        if (state.campaignState.editingCampaign.fetchState !== FETCH_STATE_SUCCESS) {
            return;
        }

        let recivedCampaignId: string = campaignId
            ? undefined
            : state.campaignState.editingCampaign.data.result.campaignId;

        yield* purchaseCampaignByIdRequest.saga({
            clientId: clientId,
            campaignId: campaignId || recivedCampaignId
        });

        yield put(changeSaveOrPurchaseProcessingAction({ isProcessing: false }));

        yield put({ type: openNotificationAction.type, isOpen: true, message: "Campaign purchased!" });

        yield put(selectPurchasedCampaignNameAction({ campaignName: mediaPlanForm.fields.campaignName.value }));

        yield put(replace(DashboardUrl.format({ clientId: clientId })));
    } catch (e) {
        console.log("Error purchasing campaign", e);
        yield put(changeSaveOrPurchaseProcessingAction({ isProcessing: false }));

        if (purchaseCampaignByIdRequest.isSagaException(e)) {
            if (e.errorResult.errorType === "response") {
                const schemeErrors: any = {};
                const response = e.errorResult.error as Response;

                const errors: any = yield response.json();

                errors.message.forEach((error: any) => {
                    // const errorKey = error.property.replace(/instance./, "");
                    const errorKey = error.argument;
                    schemeErrors[errorKey] = [error.message];
                });

                yield put(campaignMediaPlanForm.actions.validate());
                yield put(campaignCreativesForm.actions.validate());
                yield put(campaignBudgetForm.actions.validate());

                yield put(campaignMediaPlanForm.actions.setErrors({ fields: schemeErrors }));
                yield put(campaignCreativesForm.actions.setErrors({ fields: schemeErrors }));
                yield put(campaignBudgetForm.actions.setErrors({ fields: schemeErrors }));

                yield put({
                    type: openNotificationAction.type,
                    isOpen: true,
                    isWarning: true,
                    message:
                        "Please check your selection (red highlights), unable to purchase campaign in this setting."
                });

                return;
            }
        }

        yield put({
            type: openNotificationAction.type,
            isOpen: true,
            isWarning: true,
            message: "Unable to purchase campaign. Please try a bit later"
        });
    }
}
